import type { FC } from "react"
import { Autocomplete as MUIAutocomplete, Box, TextField } from "@mui/material"
import { createFilterOptions } from "@mui/material/Autocomplete"
import { Controller, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import fp from "lodash/fp"

import type { TelemetryOptionType } from "types/telemetries.types"
import type { InputRules } from "types/common.types"

import { Chip } from "widgets/styled/containers"

type OptionType = {
  label: string
  value: string
}

interface ControlledAutocompleteProps {
  name: string
  options: OptionType[]
  label?: string
  rules?: InputRules
  multiple?: boolean
}
const filter = createFilterOptions<OptionType>()

const ControlledAutocomplete: FC<ControlledAutocompleteProps> = ({
  name,
  options,
  label,
  rules,
  multiple,
}: ControlledAutocompleteProps) => {
  const { control } = useFormContext()
  const { t } = useTranslation()
  // Value selected with enter, right from the input
  const toOption = (value: string | OptionType) =>
    typeof value === "string" ? { label: value, value } : value

  return (
    <Box sx={{ width: "100%", height: "fit-content" }}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <MUIAutocomplete
            {...field}
            // A single selection is still stored as a list, the same shape as a multiple one.
            value={multiple ? field.value || [] : (field.value?.[0] ?? null)}
            multiple={multiple}
            options={options}
            getOptionLabel={(option) => {
              if (typeof option === "string") {
                return option
              }
              return option.label
            }}
            isOptionEqualToValue={(
              option: TelemetryOptionType,
              value: TelemetryOptionType,
            ) => fp.isEqual(option, value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                variant="outlined"
                required={!!rules?.required}
                sx={{ mb: 0 }}
              />
            )}
            renderTags={(tagValue, getTagProps) => {
              return tagValue.map((option, index) => (
                // eslint-disable-next-line react/jsx-key
                <Chip {...getTagProps({ index })} label={option.label} />
              ))
            }}
            onChange={(_, data) => {
              if (!multiple) {
                return field.onChange(data ? [toOption(data as string | OptionType)] : [])
              }
              return field.onChange(fp.map(toOption, data as (string | OptionType)[]))
            }}
            filterOptions={(options, params) => {
              const filtered = filter(options, params)
              const { inputValue } = params
              // Suggest the creation of a new value
              const isExisting = options.some((option) => inputValue === option.value)
              if (inputValue !== "" && !isExisting) {
                filtered.push({
                  label: `${t("generic.ADD")} "${inputValue}"`,
                  value: inputValue,
                })
              }
              return filtered
            }}
            freeSolo
            clearOnBlur
            selectOnFocus
            handleHomeEndKeys
          />
        )}
      />
    </Box>
  )
}

export default ControlledAutocomplete
